import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/motion";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ_GROUPS } from "@/data/site";

export default function FaqPage() {
  return (
    <div data-testid="faq-page">
      <Seo
        title="FAQ"
        description="Answers to common questions about North Cadence's career and organisational services, pricing, timelines, and how engagements work."
      />
      <PageHero
        eyebrow="FAQ"
        title={["Questions,", "answered."]}
        subtitle="Everything you might want to know before booking a discovery call — including honest answers about where we are as a new firm. If your question isn't here, book a call. We'd rather answer it in conversation."
      />

      {FAQ_GROUPS.map((group, gi) => (
        <section
          key={group.group}
          data-testid={`faq-group-${gi}`}
          className={`${gi % 2 === 0 ? "bg-white" : "bg-[#FEF7F2]"} py-8`}
        >
          <div className="nc-container">
            <Reveal>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value={`group-${gi}`} className="border-none">
                  <AccordionTrigger
                    data-testid={`faq-group-toggle-${gi}`}
                    className="py-6 text-left font-display text-2xl sm:text-3xl font-bold text-[#142A5E] hover:no-underline [&>svg]:h-6 [&>svg]:w-6"
                  >
                    {group.group}
                  </AccordionTrigger>
                  <AccordionContent className="pb-2 pt-4">
                    <Accordion type="single" collapsible className="w-full">
                      {group.items.map((f, i) => (
                        <AccordionItem
                          key={i}
                          value={`item-${gi}-${i}`}
                          data-testid={`faq-item-${gi}-${i}`}
                          className="border-b border-[#e7e9ef]"
                        >
                          <AccordionTrigger className="py-6 text-left font-display text-lg font-semibold text-[#1B1B1B] hover:text-[#142A5E] hover:no-underline">
                            {f.q}
                          </AccordionTrigger>
                          <AccordionContent className="pb-6 text-[15px] leading-relaxed text-[#1B1B1B]/65">
                            {f.a}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Reveal>
          </div>
        </section>
      ))}

      <section className="bg-[#142A5E] py-8">
        <div className="nc-container flex flex-col items-center gap-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Still have a question?</h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="max-w-xl text-white/70">
              Book a free discovery call and ask us directly. No prep required — just bring your situation and we'll help you figure out the right next step.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/contact"
              className="group mt-2 inline-flex items-center gap-2 rounded-full bg-[#2FA86A] px-7 py-4 font-semibold text-white transition-colors hover:bg-[#248a5a]"
            >
              Book a Free Discovery Call
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
